import { OPEN_GLOBAL_SEARCH } from '@/constants/events'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Clock, Lightbulb, Trash2, TrendingUp, Search } from 'lucide-react'
import type { NavItem } from '@vnetwork/datacentral-ui/shells'
import {
    Badge,
    Chip,
    CommandGroup,
    CommandItem,
    CommandPalette,
    Input,
    Spinner,
    foldText,
} from '@vnetwork/datacentral-ui/primitives'
import { useTypingPlaceholder } from '@/hooks/useTypingPlaceholder'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getMaskedValue } from '@/components/PrivacyField'
import { formatDate } from '@/utils/function'
import { getSearchSuggestions } from '../../services'
import { useSidebarNav } from '@/components/Sidebar/useSidebarNav'
import { SEARCH_MIN_QUERY_LENGTH } from '../../constants'
import { useGlobalSearch } from '../../hooks/useGlobalSearch'
import { SearchResult, SearchResultType } from '../../types'
import { wrapSearchResult } from '../../utils/wrap-result'
import HighlightText from '../HighlightText'

interface SearchBoxProps {
    placeholder?: string | string[]
    /** Show the scope chips (customer / service / contract / invoice). */
    showFilters?: boolean
    className?: string
}

/** Event that opens the global search palette (e.g. the mobile topbar icon). */
export { OPEN_GLOBAL_SEARCH }

const SCOPES: SearchResultType[] = [
    'customer',
    'service',
    'contract',
    'invoice',
]
const MAX_PAGES = 6

/** Visible nav leaves with a link, with their group label. */
function flattenNav(nav: NavItem[]) {
    const out: { item: NavItem; group?: string }[] = []
    for (const item of nav) {
        if (item.hidden || item.section) continue
        if (item.items?.length) {
            for (const child of item.items)
                if (!child.hidden && !child.section && child.href)
                    out.push({ item: child, group: String(item.label) })
        } else if (item.href) out.push({ item })
    }
    return out
}

/**
 * Global search as a ⌘K command palette (cmdk): app pages from the sidebar
 * nav, plus customers / services / contracts / invoices from `/search`.
 * Empty query lists recent and popular searches.
 */
const SearchBox = ({
    placeholder,
    showFilters = false,
    className = '',
}: SearchBoxProps) => {
    const { t } = useTranslation('search')
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const { nav } = useSidebarNav()

    useEffect(() => {
        const onOpen = () => setOpen(true)
        window.addEventListener(OPEN_GLOBAL_SEARCH, onOpen)
        return () => window.removeEventListener(OPEN_GLOBAL_SEARCH, onOpen)
    }, [])

    const typingPlaceholder = useTypingPlaceholder({
        placeholders: Array.isArray(placeholder)
            ? placeholder
            : [placeholder || t('search-placeholder')],
        typingSpeed: 80,
        deletingSpeed: 40,
        pauseDuration: 2000,
    })

    const {
        query,
        setQuery,
        results,
        isPending,
        selectedScopes,
        updateScopes,
        recentSearches,
        popularSearches,
        saveRecentSearch,
        clearRecent,
    } = useGlobalSearch({ autoSearch: true, debounceDelay: 400 })

    const trimmed = query.trim()
    const searching = trimmed.length >= SEARCH_MIN_QUERY_LENGTH
    const settled = searching && !isPending
    const noHits = settled && results.length === 0

    // Spelling suggestions when the search found nothing.
    const { data: suggestions = [] } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.SEARCH_SUGGESTIONS, trimmed],
            queryFn: () => getSearchSuggestions(trimmed),
            enabled: open && noHits,
            staleTime: 1000 * 60 * 5,
        },
        { showLoading: false, showError: false }
    )

    const pages = useMemo(() => {
        const q = foldText(trimmed)
        if (!q) return []
        return flattenNav(nav)
            .filter(({ item, group }) =>
                foldText(`${item.label} ${group ?? ''}`).includes(q)
            )
            .slice(0, MAX_PAGES)
    }, [nav, trimmed])

    const grouped = useMemo(() => {
        const map = new Map<SearchResultType, SearchResult[]>()
        for (const r of results) {
            const list = map.get(r.type) ?? []
            list.push(r)
            map.set(r.type, list)
        }
        return SCOPES.filter((s) => map.has(s)).map(
            (s) => [s, map.get(s)!] as const
        )
    }, [results])

    const close = useCallback(() => {
        setOpen(false)
        setQuery('')
    }, [setQuery])

    const go = (href?: string, external?: boolean) => {
        close()
        if (!href) return
        // External nav entries (e.g. the user guide) open like the sidebar's.
        if (external || /^https?:\/\//.test(href))
            window.open(href, '_blank', 'noopener,noreferrer')
        else navigate(href)
    }

    const toggleScope = (scope: SearchResultType) =>
        updateScopes(
            selectedScopes.includes(scope)
                ? selectedScopes.filter((s) => s !== scope)
                : [...selectedScopes, scope]
        )

    return (
        <>
            <div className={className}>
                <Input
                    inputSize="sm"
                    placeholder={typingPlaceholder}
                    startSlot={<Search className="w-4 h-4" />}
                    endSlot={
                        <kbd className="rounded-sm border border-border px-1 font-mono text-xs text-muted-foreground">
                            ⌘K
                        </kbd>
                    }
                    readOnly
                    role="button"
                    aria-label={t('search')}
                    onClick={() => setOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setOpen(true)
                        }
                    }}
                    wrapperClassName="cursor-pointer"
                    className="cursor-pointer"
                />
            </div>
            <CommandPalette
                open={open}
                onOpenChange={(o) => (o ? setOpen(true) : close())}
                value={query}
                onValueChange={setQuery}
                // Results are filtered by the server (and pages above).
                shouldFilter={false}
                placeholder={t('search-placeholder')}
                emptyMessage={
                    searching
                        ? t('no-results-found', { query: trimmed })
                        : t('search-hint-message')
                }
            >
                {showFilters && (
                    <div className="flex flex-wrap items-center gap-2 px-2 pb-2">
                        {SCOPES.map((scope) => (
                            <Chip
                                key={scope}
                                size="sm"
                                selected={selectedScopes.includes(scope)}
                                onClick={() => toggleScope(scope)}
                            >
                                {t(scope)}
                            </Chip>
                        ))}
                    </div>
                )}

                {!trimmed && recentSearches.length > 0 && (
                    <CommandGroup heading={t('recent-searches')}>
                        {recentSearches.slice(0, 5).map((q) => (
                            <CommandItem
                                key={`recent-${q}`}
                                value={`recent-${q}`}
                                icon={<Clock size={16} />}
                                onSelect={() => setQuery(q)}
                            >
                                {q}
                            </CommandItem>
                        ))}
                        <CommandItem
                            value="recent-clear"
                            icon={<Trash2 size={16} />}
                            onSelect={() => void clearRecent()}
                        >
                            {t('clear-all')}
                        </CommandItem>
                    </CommandGroup>
                )}
                {!trimmed && popularSearches.length > 0 && (
                    <CommandGroup heading={t('popular-searches')}>
                        {popularSearches.slice(0, 6).map((q: string) => (
                            <CommandItem
                                key={`popular-${q}`}
                                value={`popular-${q}`}
                                icon={<TrendingUp size={16} />}
                                onSelect={() => setQuery(q)}
                            >
                                {q}
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}

                {pages.length > 0 && (
                    <CommandGroup heading={t('pages', 'Trang')}>
                        {pages.map(({ item, group }) => (
                            <CommandItem
                                key={`page-${item.id}`}
                                value={`page-${item.id}`}
                                icon={item.icon}
                                description={group}
                                onSelect={() => go(item.href, item.external)}
                            >
                                {item.label}
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}

                {searching && isPending && (
                    <CommandItem
                        value="searching"
                        disabled
                        icon={<Spinner size="sm" />}
                    >
                        {`${t('searching-for')} "${trimmed}"…`}
                    </CommandItem>
                )}

                {noHits && suggestions.length > 0 && (
                    <CommandGroup heading={t('suggestions')}>
                        {suggestions.slice(0, 5).map((sg) => (
                            <CommandItem
                                key={`suggest-${sg.text}`}
                                value={`suggest-${sg.text}`}
                                icon={<Lightbulb size={16} />}
                                onSelect={() => setQuery(sg.text)}
                            >
                                {sg.text}
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}

                {settled && results.length > 0 && (
                    <div className="px-2 pb-1 text-xs text-muted-foreground">
                        {t('found-results', {
                            total: results.length,
                            query: trimmed,
                        })}
                    </div>
                )}

                {settled &&
                    grouped.map(([type, list]) => (
                        <CommandGroup
                            key={type}
                            heading={`${t(type)} (${list.length})`}
                        >
                            {list.map((r) => {
                                const line = wrapSearchResult(r, t)
                                const meta = [
                                    line.email &&
                                        getMaskedValue('email', line.email),
                                    line.created_at &&
                                        formatDate(line.created_at),
                                ]
                                    .filter(Boolean)
                                    .join(' · ')
                                return (
                                    <CommandItem
                                        key={`${type}-${r.ref_id}`}
                                        value={`${type}-${r.ref_id}`}
                                        icon={line.icon}
                                        description={
                                            <>
                                                {line.subtitle ? (
                                                    <HighlightText
                                                        text={line.subtitle}
                                                        searchQuery={trimmed}
                                                        highlightClassName="bg-warning-subtle"
                                                    />
                                                ) : null}
                                                {line.subtitle && meta
                                                    ? ' · '
                                                    : ''}
                                                {meta}
                                            </>
                                        }
                                        trailing={
                                            line.status ? (
                                                <Badge
                                                    size="sm"
                                                    tone={line.status.tone}
                                                >
                                                    {line.status.label}
                                                </Badge>
                                            ) : undefined
                                        }
                                        onSelect={() => {
                                            void saveRecentSearch(trimmed)
                                            go(line.redirect_to)
                                        }}
                                    >
                                        <HighlightText
                                            text={line.label}
                                            searchQuery={trimmed}
                                            highlightClassName="bg-warning-subtle font-semibold"
                                        />
                                    </CommandItem>
                                )
                            })}
                        </CommandGroup>
                    ))}
            </CommandPalette>
        </>
    )
}

export default SearchBox
